import React, { useState } from 'react';
import api, { handleAPIError } from '../../services/api';

interface LocationResult {
  score: number;
  location_code: number;
  location_name: string;
  location_type: string;
  country_iso_code: string;
}

interface LocationSearchResponse {
  locations: LocationResult[];
}

const LocationLookup: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.post<LocationSearchResponse>('/seotools/locations/search', {
        query: trimmed,
      });
      setResults(response.data.locations);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Location Lookup</h2>
        <p className="text-sm text-gray-500 mt-1">
          Search for DataForSEO location codes by name.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-3 items-end">
        <div className="flex-1">
          <label htmlFor="location-query" className="block text-sm font-medium text-gray-700 mb-1">
            Location Search
          </label>
          <input
            id="location-query"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. New York, United States, London..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching...
            </span>
          ) : (
            'Search'
          )}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {results !== null && results.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 text-gray-600 px-4 py-6 rounded-lg text-sm text-center">
          No results found for "{query}".
        </div>
      )}

      {results !== null && results.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.map((loc) => (
                <tr key={loc.location_code} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                    {loc.location_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                    {loc.location_code}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                    {loc.location_type}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 uppercase">
                    {loc.country_iso_code}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LocationLookup;
